import { Button, Card, CardContent, CardHeader, CardTitle } from '@sunstone/ui';
import { CheckCircle, Download } from 'lucide-react';

export function BootstrapInstructions({ deploymentType }: { deploymentType: string }) {
  const isAWS = deploymentType === 'DEPLOYMENT_TYPE_CLOUD_AWS';
  const isGCP = deploymentType === 'DEPLOYMENT_TYPE_CLOUD_GCP';
  const isAzure = deploymentType === 'DEPLOYMENT_TYPE_CLOUD_AZURE';

  const templateLabel = isAWS
    ? 'CloudFormation Template'
    : isGCP
      ? 'Deployment Manager Template'
      : isAzure
        ? 'ARM Template'
        : 'Bootstrap Template';

  const steps = isAWS
    ? [
        'Download the CloudFormation template below.',
        'Open the AWS CloudFormation console in your account.',
        'Create a new stack using the downloaded template.',
        'Wait for the stack to reach CREATE_COMPLETE status.',
        'The Lambda function in the stack will notify Sunstone automatically.',
      ]
    : isGCP
      ? [
          'Download the Deployment Manager template below.',
          'Open Google Cloud Shell or your local gcloud CLI.',
          'Run: gcloud deployment-manager deployments create sunstone-bootstrap --config template.yaml',
          'Wait for the deployment to complete.',
          'The Cloud Function in the deployment will notify Sunstone automatically.',
        ]
      : [
          'Download the ARM template below.',
          'Open the Azure Portal or Azure CLI.',
          'Deploy the template to your subscription.',
          'Wait for the deployment to complete.',
          'The Azure Function in the template will notify Sunstone automatically.',
        ];

  return (
    <Card className="border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <CheckCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          Bootstrap Required
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground leading-relaxed">
          Your deployment is ready to bootstrap. Launch the {templateLabel} in your cloud account to
          provision the required IAM roles, security groups, and resources. Uniphore Business AI
          Cloud Management will be notified automatically when setup is complete.
        </p>
        <ol className="space-y-2">
          {steps.map((step, i) => (
            <li key={i} className="flex items-start gap-3 text-sm">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400 text-xs flex items-center justify-center font-semibold mt-0.5">
                {i + 1}
              </span>
              <span className="text-muted-foreground leading-relaxed">{step}</span>
            </li>
          ))}
        </ol>
        <Button
          variant="outline"
          className="border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30"
        >
          <Download className="w-4 h-4" />
          Download {templateLabel}
        </Button>
      </CardContent>
    </Card>
  );
}
